import React, { useCallback, useEffect, useState } from 'react';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import Spinner from '../../ui/Spinner';
import { documentDesignService } from '../../../services/documentDesignService';
import { formatRelative } from '../BrandLibraryTab';
import MediaCreatorEditor from './MediaCreatorEditor';
import OwnerScopeToggle, { ownerLabel } from '../OwnerScopeToggle';
import { DEFAULT_BRIEF_PLACEHOLDER, PRESET_LABELS } from './presets';

const FONT_LINK = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@600;700;800&family=Source+Sans+3:wght@400;600;700&display=swap';

const MediaCreatorTab = () => {
  const [items, setItems] = useState([]);
  const [presets, setPresets] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('gallery');
  const [active, setActive] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [scope, setScope] = useState('mine');
  const showOwner = scope === 'all';
  const [form, setForm] = useState({
    preset: 'linkedin_banner',
    brief: '',
    tone: 'external',
    require_approved_photos: true,
  });

  useEffect(() => {
    if (document.querySelector('link[data-mc-fonts]')) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = FONT_LINK;
    link.setAttribute('data-mc-fonts', '1');
    document.head.appendChild(link);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { items: rows, presets: p, viewer: v } = await documentDesignService.listCompositions(
        scope === 'all' ? { scope } : {},
      );
      setItems(rows);
      setPresets(p);
      setViewer(v);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load compositions');
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    if (view === 'gallery') load();
  }, [load, view]);

  const openNew = () => {
    setForm({
      preset: 'linkedin_banner',
      brief: '',
      tone: 'external',
      require_approved_photos: true,
    });
    setView('new');
  };

  const generate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const comp = await documentDesignService.createComposition({
        action: 'generate',
        brief: form.brief.trim(),
        preset: form.preset,
        tone: form.tone,
        require_approved_photos: form.require_approved_photos,
      });
      setActive(comp);
      setView('editor');
    } catch (err) {
      setError(err.message || 'Generate failed');
    } finally {
      setGenerating(false);
    }
  };

  const openItem = async (id) => {
    setLoading(true);
    try {
      setActive(await documentDesignService.getComposition(id));
      setView('editor');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this design?')) return;
    await documentDesignService.deleteComposition(id);
    load();
  };

  if (view === 'editor' && active) {
    return (
      <MediaCreatorEditor
        composition={active}
        onBack={() => { setView('gallery'); setActive(null); }}
        onSaved={setActive}
      />
    );
  }

  if (view === 'new') {
    return (
      <Card className="content-card mc-wizard dd-form">
        <h2>New media</h2>
        <p className="dd-muted">Describe what you need; AI will propose copy, layout, and Brand Library photos.</p>
        {error && <div className="dd-error-inline">{error}</div>}
        <label className="mc-field">
          Size
          <select value={form.preset} onChange={(e) => setForm({ ...form, preset: e.target.value })}>
            {Object.entries(PRESET_LABELS).map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>
        </label>
        <label className="mc-field">
          Brief
          <textarea
            rows={5}
            value={form.brief}
            placeholder={DEFAULT_BRIEF_PLACEHOLDER}
            onChange={(e) => setForm({ ...form, brief: e.target.value })}
          />
        </label>
        <div className="dd-layout-options">
          <label className="dd-inline-check">
            <input
              type="radio"
              name="tone"
              checked={form.tone === 'external'}
              onChange={() => setForm({ ...form, tone: 'external', require_approved_photos: true })}
            />
            External (approved photos)
          </label>
          <label className="dd-inline-check">
            <input
              type="radio"
              name="tone"
              checked={form.tone === 'internal'}
              onChange={() => setForm({ ...form, tone: 'internal', require_approved_photos: false })}
            />
            Internal
          </label>
        </div>
        <div className="mc-wizard-actions">
          <Button variant="ghost" onClick={() => setView('gallery')}>Cancel</Button>
          <Button loading={generating} onClick={generate} disabled={form.brief.trim().length < 8}>
            Generate with AI
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <>
      <div className="dd-tab-actions">
        <p>Create banners, slides, social posts, and one-pagers from the Brand Library.</p>
        <div className="dd-header-tools">
          <OwnerScopeToggle viewer={viewer} scope={scope} onChange={setScope} disabled={loading} />
          <Button onClick={openNew}>New design</Button>
        </div>
      </div>
      {error && <div className="dd-error-inline">{error}</div>}
      <Card className="content-card">
        {loading ? (
          <div className="loading-container"><Spinner size="large" /><p>Loading designs…</p></div>
        ) : items.length === 0 ? (
          <div className="no-data">
            <h3>{showOwner ? 'No media creations yet' : 'You have no media creations yet'}</h3>
            <p>Start from a brief with Generate with AI.</p>
          </div>
        ) : (
          <table className="dd-doc-table">
            <thead>
              <tr>
                <th>Title</th>
                {showOwner && <th>Owner</th>}
                <th>Size</th>
                <th>Status</th>
                <th>Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} onClick={() => openItem(row.id)}>
                  <td><div className="dd-doc-title">{row.title}</div><div className="dd-muted">{row.brief?.slice(0, 80)}</div></td>
                  {showOwner && <td className="dd-muted">{ownerLabel(row)}</td>}
                  <td className="dd-muted">{PRESET_LABELS[row.preset] || row.preset}</td>
                  <td>{row.status}</td>
                  <td className="dd-muted">{formatRelative(row.updated_at)}</td>
                  <td className="dd-row-actions">
                    <Button size="sm" variant="ghost" onClick={(e) => remove(row.id, e)}>Delete</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
};

export default MediaCreatorTab;
